import { getCdnHost } from '@/config/apiHosts';
import { TodoTaskInfo, TaskType } from '@/redux/reducers/todoTask';

enum AudioType {
  // 系统固定音频
  CRS_ORDER_CREATE = 1, // 中央预订新订单
  CRS_ORDER_CANCEL, // 中央预订取消
  CRS_ORDER_AUTO_RECEIVE, // 中央预订自动接单
  CALL_HELP_SERVICE, // 呼叫客房服务
  MT_ZL_ORDER_CREATE, // 美团直连新订单
  MT_ZL_ORDER_CANCEL, // 美团直连直接取消
  XC_ZL_ORDER_CREATE, // 携程直连新订单
  XC_ZL_ORDER_CANCEL, // 携程直连直接取消
  YGP_GUEST_GAME_START, // 宾客赛事提醒
  DY_ZL_ORDER_CREATE, // 抖音直连新订单
  DY_ZL_ORDER_CANCEL, // 抖音直连直接取消
  SELF_SERVICE_ORDER_CREATE, // 自助点餐待备餐

  // 自定义文本播报音频
  FEATURE_ROOM_NUM, // 远期房量提醒
}

// 是否是自定义文本播报语音类型
export const isCustomAudioType = (type: AudioType) => {
  const CustomAudioTypeMap = {
    [AudioType.FEATURE_ROOM_NUM]: true, // 远期房量异常提醒
  };
  return CustomAudioTypeMap[type];
};

// 任务对应固定音频文件地址
const AudioSourceUrlMap = {
  // 中央预订新订单
  [AudioType.CRS_ORDER_CREATE]: `${getCdnHost()}/upload/prod/pms/2022-08-31/oWlySzpZ7OWlYYQwdIN.flac`,
  // 中央预订取消
  [AudioType.CRS_ORDER_CANCEL]: `${getCdnHost()}/upload/prod/pms/2022-08-31/7pLzjFBQKTP3VtDB7YZ.flac`,
  // 中央预订自动接单
  [AudioType.CRS_ORDER_AUTO_RECEIVE]: `${getCdnHost()}/upload/prod/pms/2022-09-07/O51GcNLNAfYnMWlr12i.mp3`,
  // 美团直连新订单
  [AudioType.MT_ZL_ORDER_CREATE]: `${getCdnHost()}/upload/prod/pms/2022-08-31/kzxFfUnTAo9wdFraB1D.mp3`,
  // 美团直连取消
  [AudioType.MT_ZL_ORDER_CANCEL]: `${getCdnHost()}/upload/prod/pms/2022-08-31/dUDPbvTTGH5kl2j8TL7.mp3`,
  // 携程直连新订单
  [AudioType.XC_ZL_ORDER_CREATE]: `${getCdnHost()}/upload/prod/pms/2022-10-17/0DUkSzgteK2RLvr8Y0i.mp3`,
  // 携程直连直接取消
  [AudioType.XC_ZL_ORDER_CANCEL]: `${getCdnHost()}/upload/prod/pms/2022-10-17/chUsGMG1vKZZeGENDBR.mp3`,
  // 呼叫服务
  [AudioType.CALL_HELP_SERVICE]: `${getCdnHost()}/upload/prod/pms/2022-08-31/tGNux2qySDyDGOzNYj2.wav`,
  // 赛事提醒
  [AudioType.YGP_GUEST_GAME_START]: `${getCdnHost()}/upload/prod/ypms/2023-04-20/RXhQ1s2XLxJu0HWxTNX.mp3`,
  // 抖音直连新订单
  [AudioType.DY_ZL_ORDER_CREATE]: `${getCdnHost()}/upload/prod/ypms/2023-12-11/Lb7pPmJJ3sXKA49rZh2.m4a`,
  // 抖音直连直接取消
  [AudioType.DY_ZL_ORDER_CANCEL]: `${getCdnHost()}/upload/prod/ypms/2023-12-11/McGzRi5CnJstCTNVznn.m4a`,
  // 自助点餐待备餐
  [AudioType.SELF_SERVICE_ORDER_CREATE]: `${getCdnHost()}/upload/prod/ypms/2023-12-26/FCx90QxjRIGzPsLbQWX.mp3`,
};

const CacheAudioBufferMap = {};

class TodoAudioPlayer {
  // audio instance context
  private audioContext: AudioContext;

  // play audio type queue
  private audioTypeQueue: Array<{ type: AudioType; id: any }> = []; //  添加id唯一标识，因type相同但是自定义播报文本动态化

  // audio play status
  private isPlaying: boolean;

  // audio play text map
  private customAudioTextMap: {}; // { id: text }

  constructor() {
    this.isPlaying = false;
    this.audioTypeQueue = [];
    this.customAudioTextMap = {};
    this.initContext();
    this.initByUser();
  }

  // init after user gesture
  private initByUser = () => {
    const context = this.audioContext;
    if (context.state === 'suspended') {
      const handler = () => {
        context.resume().then(() => {
          this.playNext();
        });
        document.removeEventListener('mousedown', handler);
      };
      document.addEventListener('mousedown', handler);
    }
  };

  private initContext = () => {
    const audioContext = new window.AudioContext();
    this.audioContext = audioContext;
  };

  // 获取音频文件
  private loadAudioBuffer: (type: AudioType) => Promise<ArrayBuffer> = (type) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', AudioSourceUrlMap[type]);
      xhr.responseType = 'arraybuffer';
      xhr.onload = () => {
        resolve(xhr.response);
      };
      xhr.onerror = reject;
      xhr.send();
    });
  };

  // 解析音频
  private decodeAudioBuffer: (arrayBuffer: ArrayBuffer) => Promise<AudioBuffer> = (arrayBuffer) => {
    return new Promise((resolve, reject) => {
      const context = this.audioContext;
      context.decodeAudioData(arrayBuffer, resolve, reject);
    });
  };

  // 获取音频buffer
  private getAudioBuffer = async (type: AudioType) => {
    const cacheBuffer = CacheAudioBufferMap[type];
    if (cacheBuffer) return cacheBuffer;
    const arrayBuffer = await this.loadAudioBuffer(type);
    const audioBuffer = await this.decodeAudioBuffer(arrayBuffer);
    CacheAudioBufferMap[type] = audioBuffer;
    return audioBuffer;
  };

  // 添加播放任务
  public addTask = (type: AudioType, content: string, id: any) => {
    if (AudioType[type]) {
      this.customAudioTextMap = { ...this.customAudioTextMap, [id]: content };
      this.audioTypeQueue.push({ type, id });
      this.playNext();
    }
  };

  // 音频播放完成回调
  private handlePlayEnd = () => {
    setTimeout(() => {
      this.isPlaying = false;
      this.playNext();
      // wait to play next
    }, 1.5 * 1000);
  };

  // 播放下一个任务音频
  private playNext = async () => {
    const contextState = this.audioContext.state;
    // 当前正在播放 或者 尚未初始化
    if (this.isPlaying || contextState === 'suspended') return;
    const queueObj = this.audioTypeQueue.shift();

    if (queueObj !== undefined) {
      try {
        const { type: audioType, id } = queueObj;
        // 判断是否是自定义音频
        const isCustom = isCustomAudioType(audioType);

        this.isPlaying = true;
        if (isCustom) {
          this.playCustomTextAudio(this.customAudioTextMap[id]);
        } else {
          const audioBuffer = await this.getAudioBuffer(audioType);
          this.playAudio(audioBuffer);
        }
      } catch (e) {
        // 当前播放错误时 播放下一个
        this.isPlaying = false;
        this.playNext();
      }
    }
  };

  // 播放音频buffer
  private playAudio = (audioBuffer) => {
    const context = this.audioContext;
    const audioSource = context.createBufferSource();
    audioSource.connect(context.destination);
    // 播放完毕 自动播放下一个
    audioSource.onended = this.handlePlayEnd;
    audioSource.buffer = audioBuffer;
    audioSource.start(0);
  };

  /**
   * 自定义语音播报 异步
   * 搭配async await
   * @param msg 播报的信息
   */
  private speakVoice = (text: string) => {
    const that = this;
    return new Promise((resolve) => {
      const speech = new SpeechSynthesisUtterance(text);
      // 设置兼容中文
      const voices = window?.speechSynthesis?.getVoices();
      // eslint-disable-next-line prefer-destructuring
      speech.voice = voices.filter(function (voice) {
        return voice.localService === true && voice.lang === 'zh-CN';
      })[0];
      // 播放结束回调
      speech.onend = () => {
        that.isPlaying = false;
        that.playNext();
        resolve(true);
      };
      window?.speechSynthesis?.speak(speech);
    });
  };

  private virtualClick = (func, text: string) => {
    const button = document.createElement('button');
    button.textContent = '点击我';
    // 添加点击事件处理程序
    button.addEventListener('click', function () {
      if (func) func(text);
    });
    // 模拟用户点击事件
    const event = new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    button.dispatchEvent(event);
  };

  private playCustomTextAudio = (text: string) => {
    this.virtualClick(this.speakVoice, text);
  };
}

// 待办任务对应的音频
export const TaskAudioGetter: {
  [type: number]: (task: TodoTaskInfo) => AudioType;
} = {
  // 静态音频(mp3 etc.)
  [TaskType.CHA_CENTRAL_NEW]: () => AudioType.CRS_ORDER_CREATE, // 中央预订申请接单
  [TaskType.CHA_CENTRAL_NEW_AUTO]: () => AudioType.CRS_ORDER_AUTO_RECEIVE, // 中央预订自动接单
  [TaskType.CHA_CENTRAL_CANCEL]: () => AudioType.CRS_ORDER_CANCEL, // 中央预订申请取消
  [TaskType.MT_ZL_NEW_AUTO]: () => AudioType.MT_ZL_ORDER_CREATE, // 美团直连自动接单
  [TaskType.MT_ZL_CANCEL]: () => AudioType.MT_ZL_ORDER_CANCEL, // 美团直连订单取消
  [TaskType.XC_ZL_NEW_AUTO]: () => AudioType.XC_ZL_ORDER_CREATE, // 携程直连自动接单
  [TaskType.XC_ZL_CANCEL]: () => AudioType.XC_ZL_ORDER_CANCEL, // 携程直连订单取消
  [TaskType.DY_ZL_NEW_AUTO]: () => AudioType.DY_ZL_ORDER_CREATE, // 携程直连自动接单
  [TaskType.DY_ZL_CANCEL]: () => AudioType.DY_ZL_ORDER_CANCEL, // 携程直连订单取消
  [TaskType.CUS_CALL_SERV]: () => AudioType.CALL_HELP_SERVICE, // 呼叫服务
  [TaskType.CUS_YOOPLAY_MSG]: () => AudioType.YGP_GUEST_GAME_START, // 赛事提醒
  [TaskType.GOODS_SELF_WAIT_PREPARE]: () => AudioType.SELF_SERVICE_ORDER_CREATE, // 自助点餐待备餐
  // 自定义文本音频播报
  [TaskType.FEATURE_ROOM_NUM_ALERT]: () => AudioType.FEATURE_ROOM_NUM, // 远期房量异常提醒
};

export default TodoAudioPlayer;
